import { useState, useCallback } from 'react';
import { AppIdeaRequest, AppIdeaGenerationState, GenerationTask, GenerationStatus, ProcessingStatus } from '@/types/app-ideas-types';
import { useAppIdeaTextGeneration } from './use-app-idea-text-generation';
import { useAppIdeaResearchGeneration } from './use-app-idea-research-generation';
import { useAppIdeaSubmission } from './use-app-idea-submission';
import { formatProblemStatementForApi } from '@/utils/app-idea-helpers';
import { useQueryClient } from '@tanstack/react-query';

const initialTasks: GenerationTask[] = [
  { id: 'title', name: 'Generating Title', status: 'pending' },
  { id: 'description', name: 'Generating Description', status: 'pending' },
  { id: 'research', name: 'Performing Research', status: 'pending' },
  { id: 'submission', name: 'Saving Idea', status: 'pending' },
];

export const useAppIdeaGeneration = () => {
  const [state, setState] = useState<AppIdeaGenerationState>({
    status: 'idle',
    progress: 0,
    tasks: initialTasks,
    currentTaskDescription: 'Getting ready...',
    error: null,
    generatedIdeaId: null,
  });

  const { generateTitle, generateDescription } = useAppIdeaTextGeneration();
  const { generateResearch } = useAppIdeaResearchGeneration();
  const { mutateAsync: submitIdea } = useAppIdeaSubmission();
  const queryClient = useQueryClient();

  const updateTaskStatus = (taskId: GenerationTask['id'], status: GenerationStatus, error?: string) => {
    setState(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === taskId ? { ...t, status } : t),
      ...(error && { error }),
    }));
  };

  const startGeneration = useCallback(async (request: AppIdeaRequest) => {
    setState({
      status: 'processing',
      progress: 0,
      tasks: initialTasks.map(t => ({...t, status: 'pending'})),
      currentTaskDescription: 'Starting generation process...',
      error: null,
      generatedIdeaId: null,
    });
    
    try {
      const problemStatement = formatProblemStatementForApi(request);
      
      // Step 1: Generate Title, Description, and Research in parallel
      setState(prev => ({ ...prev, progress: 10, currentTaskDescription: 'Generating initial concepts...' }));
      updateTaskStatus('title', 'in-progress');
      updateTaskStatus('description', 'in-progress');
      updateTaskStatus('research', 'in-progress');

      const [title, description, research] = await Promise.all([
        generateTitle(problemStatement).then(res => { updateTaskStatus('title', 'completed'); return res; }),
        generateDescription(problemStatement).then(res => { updateTaskStatus('description', 'completed'); return res; }),
        generateResearch(problemStatement).then(res => { 
          updateTaskStatus('research', 'completed'); 
          console.log('[AppIdeaGeneration] Research completed:', {
            research: res,
            hasCitations: !!res.citations,
            citationsCount: res.citations?.length || 0,
            hasRelatedQuestions: !!res.relatedQuestions,
            relatedQuestionsCount: res.relatedQuestions?.length || 0
          });
          return res; 
        }),
      ]);
      
      setState(prev => ({ ...prev, progress: 75, currentTaskDescription: 'Finalizing and saving your idea...' }));

      // Step 2: Submit to database
      updateTaskStatus('submission', 'in-progress');
      const ideaId = await submitIdea({ request, title, description, research });
      updateTaskStatus('submission', 'completed');
      
      // Step 3: Wait for cache invalidation to complete
      setState(prev => ({ ...prev, progress: 90, currentTaskDescription: 'Updating app ideas list...' }));
      
      // Wait for the cache to be invalidated and refetched
      await queryClient.invalidateQueries({ queryKey: ['app-ideas'] });
      await queryClient.refetchQueries({ queryKey: ['app-ideas'] });
      
      setState(prev => ({ 
        ...prev, 
        progress: 100,
        status: 'success', 
        generatedIdeaId: ideaId,
        currentTaskDescription: 'All done!',
      }));

    } catch (err: any) {
      console.error('[AppIdeaGeneration] Generation failed:', err);
      const failedTask = state.tasks.find(t => t.status === 'in-progress')?.id;
      if (failedTask) {
        updateTaskStatus(failedTask, 'failed', err.message);
      }
      setState(prev => ({
        ...prev,
        status: 'error',
        error: err.message || 'An unknown error occurred.',
        currentTaskDescription: 'Something went wrong.'
      }));
    }
  }, [generateTitle, generateDescription, generateResearch, submitIdea, queryClient]);

  const reset = () => {
    setState({
      status: 'idle',
      progress: 0,
      tasks: initialTasks,
      currentTaskDescription: 'Getting ready...',
      error: null,
      generatedIdeaId: null,
    });
  };

  return { state, startGeneration, reset };
};
